import { useState, useEffect, useRef } from "react";
import NotificationPopover from "./NotificationPopover";
import translations from "./translations.json";
import "./css/animations.css";
import "./css/notification.css";

const useNotification = () => {
  const lang = localStorage.getItem("preferredLanguage") || "nl";
  const t = translations[lang].notifications;

  const [notifications, setNotifications] = useState([]);
  const timeIntervalRef = useRef(null);
  const normalizeMessage = (msg) => {
    if (msg == null) return "";
    if (typeof msg === "string" || typeof msg === "number") return String(msg);
    if (typeof msg === "object") {
      // Prefer a human label when present
      if ("name" in msg && msg.name) return String(msg.name);
      try {
        return JSON.stringify(msg);
      } catch {
        return "[object]";
      }
    }
    return String(msg);
  };
  const triggerNotification = (message, type) => {
    const safeMessage = normalizeMessage(message);

    if (type === "time") {
      const id = Date.now();
      setNotifications((prev) => [
        ...prev,
        { id, message: safeMessage, type, countdown: 120 },
      ]);
    } else {
      setNotifications((prev) => {
        const isDuplicate = prev.some(
          (n) => n.message === safeMessage && n.type === type
        );
        if (isDuplicate) {
          return prev.map((n) =>
            n.message === safeMessage && n.type === type
              ? { ...n, count: (n.count || 1) + 1 }
              : n
          );
        }
        const id = Date.now();
        return [...prev, { id, message: safeMessage, type }];
      });
    }
  };

  useEffect(() => {
    const normal = notifications.filter((n) => n.type !== "time");
    if (normal.length > 0) {
      const timer = setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.type === "time"));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notifications]);

  useEffect(() => {
    const hasTime = notifications.some((n) => n.type === "time");
    if (!hasTime) return;

    if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
    timeIntervalRef.current = setInterval(() => {
      setNotifications((prev) => {
        let addSuccess = false;
        const next = prev
          .map((n) => {
            if (n.type !== "time") return n;
            if (n.countdown > 1) return { ...n, countdown: n.countdown - 1 };
            addSuccess = true; // expired
            return null;
          })
          .filter(Boolean);

        if (addSuccess) {
          next.push({
            id: Date.now(),
            message: normalizeMessage(t.savedMessage),
            type: "success",
          });
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(timeIntervalRef.current);
  }, [notifications, t.savedMessage]);

  const NotificationComponent = () => (
    <div className="notification-component">
      {notifications.map((n) => (
        <NotificationPopover
          key={n.id}
          message={n.message}
          type={n.type}
          countdown={n.countdown}
        />
      ))}
    </div>
  );

  return { triggerNotification, NotificationComponent };
};

export default useNotification;
